// Importamos React y useState para manejar estados locales
import { useState } from "react";
/* import { useNavigate } from "react-router-dom"; */
// Importamos componentes reutilizables e íconos
import { Input } from "../../ui";
import { Eye, EyeOff } from "lucide-react";

// Importamos funciones de validación
import {
  ValidateCIF,
  ValidateDNI,
  ValidateNIE,
  isValidPhone,
  isValidPostalCode,
  isValidEmail,
  isValidContrasena,
} from "../../validation/validations";

// Importamos tipos @/typescript y componentes adicionales
import { AlertaType, FormularioPaso1Props } from "@/types";
import SelectDniNie from "./validacionesFormularioPaso1/SelectDniNie";
import Alerta from "../../alert/Alerta";

// 🟢 URL base del backend definida en .env
import { safeFetch } from "@/api/safeFetch";



// Componente principal del formulario Paso 1
const FormularioPaso1: React.FC<FormularioPaso1Props> = ({
  formData1,           // Objeto con los datos del formulario
  setFormData1,        // Función para actualizar los datos del formulario
  isEmpresa,           // Indica si es empresa
  handleToggleEmpresa, // Función para cambiar estado de empresa
  errors,              // Objeto de errores del formulario
  setErrors,           // Función para actualizar errores
  userName,            // Usuario logueado (si existe)
  roles,                // roles del usuario logueado
}) => {

  const value = formData1.idSelectDniNie; // Valor del select DNI/NIE
  const [showPassword, setShowPassword] = useState(false); // Controles visibilidad contraseña

  const [alerta, setAlerta] = useState<{ mensaje: string; type: AlertaType } | null>(null); // Alertas visuales
  /* const [dniNieBloqueado, setDniNieBloqueado] = useState<boolean>(!!userName); */ // Bloqueo campo DNI/NIE

  // Toggle para mostrar/ocultar contraseña
  const togglePasswordVisibility = () => setShowPassword(!showPassword);


  const esLogueado = !!userName;
  const esUsuarioNormal = roles?.includes("usuario") ?? false;
  /*   const esProfesional = roles?.includes("profesional");
    const esAdmin = roles?.includes("admin") || roles?.includes("superadmin"); */

  // Función general para manejar cambios en inputs y select
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let sanitizedValue = value.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s@._-]/g, ""); // Sanitiza caracteres no permitidos

    // Convertir a mayúsculas campos específicos
    if (["cif", "dniNie"].includes(name)) sanitizedValue = sanitizedValue.toUpperCase();
    if (name === "numero_telefono") sanitizedValue = value.replace(/[^0-9]/g, ""); // Solo números
    if (name === "contrasena" || name === "confirmarContrasena") sanitizedValue = value.replace(/[^A-Za-z0-9&#]/g, ""); // Restricciones contraseña

    // Actualizamos el estado del formulario con el valor saneado
    setFormData1(prev => ({ ...prev, [name]: sanitizedValue }));
  };

  // Maneja cambios en el select DNI/NIE
  const handleSelectChange = (selectedType: string) => {
    setFormData1(prev => ({ ...prev, idSelectDniNie: selectedType, dniNie: "" }));
    setErrors?.(prev => ({ ...prev, dniNie: false }));
  };


  // Validación del teléfono
  const handlePhoneValidation = () => {
    const telefono = formData1.numero_telefono ?? "";
    if (telefono.length > 0) {
      const valid = isValidPhone(telefono);
      setErrors?.(prev => ({ ...prev, numero_telefono: !valid }));
      if (!valid) setFormData1(prev => ({ ...prev, numero_telefono: "" }));
    }
  };

  // Validación del DNI/NIE, con soporte para usuarios logueados
  const handleDniNieValidation = async () => {
    const { idSelectDniNie, dniNie, usuario_id } = formData1;

    // 1️⃣ Verificar que no esté vacío y sea válido
    if (!dniNie?.trim()) {
      setErrors?.(prev => ({ ...prev, dniNie: true }));
      setAlerta({ mensaje: "El DNI/NIE no puede estar vacío.", type: "dni" });
      return;
    }

    let isValid = false;
    if (idSelectDniNie?.toLowerCase() === "dni") isValid = ValidateDNI(dniNie);
    else if (idSelectDniNie?.toLowerCase() === "nie") isValid = ValidateNIE(dniNie);

    if (!isValid) {
      setErrors?.(prev => ({ ...prev, dniNie: true }));
      setFormData1(prev => ({ ...prev, dniNie: "" }));
      setAlerta({ mensaje: "DNI/NIE inválido.", type: "dni" });
      return;
    }

    // 2️⃣ Verificar en backend si ya existe
    try {
      const query = `/api/verificar-dni?dniNie=${encodeURIComponent(dniNie)}` +
        (usuario_id ? `&usuarioId=${usuario_id}` : "");

      const resp = await safeFetch(query);

      if (!resp.ok) {
        setAlerta({ mensaje: "Error de conexión", type: "error" });
        return;
      }
      const data = await resp.json();

      // 2a) DNI/NIE existe en otro usuario
      if (data.existe && !data.esPropio) {
        setErrors?.(prev => ({ ...prev, dniNie: true }));
        if (!userName) setFormData1?.(prev => ({ ...prev, dniNie: "" }));
        setAlerta({ mensaje: "Este DNI/NIE ya está registrado por otro usuario.", type: "dni" });
        return;
      }


      // 2c) Caso usuario logueado con propio DNI
      if (data.existe && data.esPropio) {
        setErrors?.(prev => ({ ...prev, dniNie: false }));
        setAlerta(null);
        return;
      }

    } catch (err) {
      console.error("Error verificando el DNI/NIE:", err);
      setAlerta({ mensaje: "Error al verificar el DNI/NIE. Intenta más tarde.", type: "error" });
    }
  };

  //Email repetido en la suscripcion
  // Validación del email con verificación en backend
  const handleEmailValidation = async () => {
    const email = formData1.email?.trim() ?? "";

    // 1️⃣ Verificar que no esté vacío
    if (!email) {
      setErrors?.(prev => ({ ...prev, email: true }));
      setAlerta({ mensaje: "El correo no puede estar vacío.", type: "email" });
      return;
    }

    // 2️⃣ Verificar formato
    if (!isValidEmail(email)) {
      setErrors?.(prev => ({ ...prev, email: true }));
      setAlerta({ mensaje: "Correo electrónico inválido.", type: "email" });
      return;
    }

    // 3️⃣ Consultar backend si el correo ya existe
    try {
      const query = `/api/verificar-email?email=${encodeURIComponent(email)}` +
        (formData1.usuario_id ? `&usuarioId=${formData1.usuario_id}` : "");

      const resp = await safeFetch(query);

      if (!resp.ok) {
        setAlerta({ mensaje: "Error al verificar el correo", type: "error" });
        return;
      }

      const data = await resp.json();

      if (data.existe && !data.esPropio) {
        setErrors?.(prev => ({ ...prev, email: true }));
        setFormData1(prev => ({ ...prev, email: "" }));
        setAlerta({ mensaje: "Este correo ya está registrado por otro usuario.", type: "email" });
      } else {
        setErrors?.(prev => ({ ...prev, email: false }));
        setAlerta(null);
      }


    } catch (err) {
      console.error("Error verificando el correo:", err);
      setAlerta({ mensaje: "Error al verificar el correo. Intenta más tarde.", type: "error" });
    }

  };


  // Validaciones individuales de campos
  const handleCifValidation = () => {
    const cif = formData1.cif?.trim() ?? "";
    const valid = ValidateCIF(cif) && cif !== "";
    setErrors?.(prev => ({ ...prev, cif: !valid }));
    if (!valid) setFormData1(prev => ({ ...prev, cif: "" }));
  };

  const handlePostalCodeValidation = () => {
    const valid = isValidPostalCode(formData1.postalCode ?? "");
    setErrors?.(prev => ({ ...prev, postalCode: !valid }));
  };




  const handleContrasenaValidacion = () => {
    const password = formData1.contrasena ?? "";
    const confirmar = formData1.confirmarContrasena ?? "";

    const errorMensaje = isValidContrasena(password);

    setErrors?.(prev => ({
      ...prev,
      contrasena: !!errorMensaje,
      confirmarContrasena: confirmar.length > 0 && password !== confirmar,
    }));

    if (errorMensaje) {
      setAlerta({ mensaje: errorMensaje, type: "error" });
      return;
    }

    if (confirmar.length > 0 && password !== confirmar) {
      setAlerta({ mensaje: "Las contraseñas no coinciden", type: "error" });
      return;
    }

    setAlerta(null);
  };




  const handleNombreEmpresaValidation = () => {
    const nombreEmpresa = formData1.nombre_empresa?.trim() ?? "";
    const valid = nombreEmpresa !== "";
    setErrors?.(prev => ({ ...prev, nombre_empresa: !valid }));
    if (!valid) setFormData1(prev => ({ ...prev, nombre_empresa: "" }));
  };

  // Renderizado principal del formulario

  return (
    <div className="grid grid-cols-12 gap-6 px-6 justify-center min-w-screen-xs">
      <h1 className="text-center text-2xl xs:mx-1 col-span-12 md:col-span-6">
        <strong>Rellena los campos</strong>
      </h1>

      {/* Toggle empresa solo para usuarios nuevos */}
      <div className="col-span-full">
        <label className="inline-flex items-center cursor-pointer" htmlFor="empresaToggle">
          <input
            type="checkbox"
            id="empresaToggle"
            className="sr-only peer"
            checked={isEmpresa}
            onChange={handleToggleEmpresa}
          />
          <div className="relative w-11 h-6 bg-zinc-400 rounded-full border-2 border-white peer-checked:bg-blue-700 after:content-[''] after:absolute after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
          <span className="ms-3 text-xl font-medium text-gray-900">
            <strong>Empresa</strong>
          </span>
        </label>
      </div>


      {/* Campos de texto */}
      <div className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3">
        <Input
          type="text"
          name="nombre"
          value={formData1.nombre}
          onChange={handleChange}
          readOnly={esLogueado && !esUsuarioNormal}
          disabled={false}
          placeholder="Nombre..."
          required
          error={errors!.nombre}
          className={`${esLogueado && !esUsuarioNormal ? 'dark:bg-blue-100 text-gray-600' : ''}`}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3">
        <Input
          type="text"
          id="apellidos"
          name="apellidos"
          value={formData1.apellidos}
          onChange={handleChange}
          readOnly={esLogueado && !esUsuarioNormal}
          placeholder="Apellidos..."
          required
          error={errors!.apellidos}
          className={`${esLogueado && !esUsuarioNormal ? "dark:bg-blue-100 text-gray-600" : ""}`}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3">
        <Input
          name="email"
          id="email"
          placeholder={errors!.email ? "Correo inválido" : "Correo electrónico"}
          type="email"
          value={formData1.email}
          onChange={handleChange}
          disabled={!!userName}
          onBlur={handleEmailValidation}
          required
          error={errors!.email}
          className={`${!!userName ? "dark:bg-blue-100 text-gray-600" : ""}`}
        />

      </div>

      <div className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3">
        <Input
          type="text"
          name="numero_telefono"
          id="numero_telefono"
          placeholder={errors!.numero_telefono ? "Teléfono inválido" : "Teléfono"}
          value={formData1.numero_telefono}
          onChange={handleChange}
          onBlur={handlePhoneValidation}
          error={errors!.numero_telefono}
          required
          maxLength={9}
        />
      </div>

      {/* Selección DNI/NIE */}
      <div className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3">
        <SelectDniNie
          id="idSelectDniNie"
          value={value || ''}
          name="idSelectDniNie"
          onChange={handleSelectChange}
          opciones={['DNI', 'NIE']}
          error={!!errors?.dniNie}
          placeholder="DNI / NIE"
          className={`${!!userName && (roles?.length ?? 0) > 0 && !roles?.includes("usuario") ? 'dark:bg-blue-100 text-gray-600' : ''}`}
          disabled={!!userName && (roles?.length ?? 0) > 0 && !roles?.includes("usuario")}

        />
      </div>

      <div className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3">
        <Input
          id="dniNie"
          name="dniNie"
          placeholder={errors?.dniNie ? "DNI/NIE inválido" : "DNI/NIE"}
          value={formData1.dniNie}
          onChange={handleChange}
          readOnly={esLogueado && !esUsuarioNormal}
          disabled={false}
          onBlur={!esLogueado || esUsuarioNormal ? handleDniNieValidation : undefined}
          required={!esLogueado || esUsuarioNormal}
          error={!!errors?.dniNie}
          className={`${esLogueado && !esUsuarioNormal ? 'dark:bg-blue-100 text-gray-600' : ''}`}
        />
      </div>


      {/* Campos adicionales */}
      <div className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3">
        <Input
          type="text"
          id="calle"
          name="calle"
          value={formData1.calle}
          onChange={handleChange}
          placeholder="Dirección..."
          required
          error={errors!.calle}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3">
        <Input
          type="text"
          id="locality"
          name="locality"
          value={formData1.locality}
          onChange={handleChange}
          placeholder="Localidad..."
          required
          error={errors!.locality}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3">
        <Input
          type="text"
          id="province"
          name="province"
          value={formData1.province}
          onChange={handleChange}
          placeholder="Provincia..."
          required
          error={errors!.province}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3">
        <Input
          type="text"
          id="postalCode"
          name="postalCode"
          value={formData1.postalCode}
          onChange={handleChange}
          onBlur={handlePostalCodeValidation}
          placeholder={errors!.postalCode ? "Código postal inválido" : "Código postal"}
          required
          error={errors!.postalCode}
          maxLength={5}
        />

      </div>

      {!userName && (
        <>
          <div className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 relative">
            <div className="relative">
              <Input
                type="text"
                name="contrasena"
                placeholder={
                  errors!.contrasena
                    ? "Contraseña inválida"
                    : "Contraseña A-Za-z0-9, &, #, _"
                }
                value={formData1.contrasena}
                onChange={handleChange}
                onBlur={handleContrasenaValidacion}
                required
                error={errors!.contrasena}
                className={`pr-10 ${!showPassword ? "password-hidden" : ""}`}
                autoComplete="new-password"
              />

              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500"
              >
                {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
            </div>
          </div>

          <div className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3">
            <div className="relative">
              <Input
                type="text"
                name="confirmarContrasena"
                placeholder={
                  errors?.confirmarContrasena
                    ? "No coinciden"
                    : "Confirmar contraseña"
                }
                value={formData1.confirmarContrasena}
                onChange={handleChange}
                onBlur={handleContrasenaValidacion}
                required
                error={errors!.confirmarContrasena}
                className={`pr-10 ${!showPassword ? "password-hidden" : ""}`}
                autoComplete="new-password"
              />

              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500"
              >
                {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
            </div>
          </div>
        </>
      )}


      {/* Campos empresa SIEMPRE renderizados */}
      <div className={`col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 transition-all duration-500 ${isEmpresa ? "opacity-100" : "opacity-0 pointer-events-none"}`} >
        <Input
          type="text"
          id="nombre_empresa"
          name="nombre_empresa"
          value={formData1.nombre_empresa}
          onChange={handleChange}
          onBlur={handleNombreEmpresaValidation}
          placeholder="Nombre de la empresa..."
          required={isEmpresa}
          error={errors!.nombre_empresa}
        />
      </div>

      <div className={`col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 transition-all duration-500 ${isEmpresa ? "opacity-100" : "opacity-0 pointer-events-none"}`} >
        <Input
          type="text"
          id="cif"
          name="cif"
          value={formData1.cif}
          onChange={handleChange}
          onBlur={handleCifValidation}
          placeholder="CIF..."
          required={isEmpresa}
          error={errors!.cif}
        />
      </div>

      {alerta && (
        <Alerta
          mensaje={alerta.mensaje}
          type={alerta.type}
          onClose={() => setAlerta(null)}
          className="max-w-md mx-auto mb-4"
        />
      )}



    </div>
  );
};

export default FormularioPaso1;
